import React, { useContext } from 'react';
import { CartContext } from '@repo/contexts/CartContext';
import { CustomerContext } from '@repo/contexts/CustomerContext';
import { LocaleContext } from '@repo/contexts/LocaleContext';
import { TrackClickAfterSearch } from '@repo/utils/algoliaAnalytics';
import getInternalLink from '@repo/utils/getInternalLink';
import getPercentageDiscountPrice from '@repo/utils/getPercentageDiscountPrice';
import LinkComponent from '@repo/ui-components/LinkComponent';

interface Variant {
  price: number;
  compareAtPrice?: {
    amount: number;
  };
}

interface Hit {
  title: string;
  excerpt?: string;
  image: string;
  slug?: string;
  link?: string;
  variants?: Variant[];
  __queryID?: string;
  cadPrice?: number;
  cadCompareAtPrice?: number;
}

interface SearchFormHitProps {
  hit: Hit;
  insights: () => void;
}

const SearchFormHit: React.FC<SearchFormHitProps> = ({ hit, insights }) => {
  const {
    title,
    excerpt = '',
    image,
    slug = '',
    link = '',
    variants = [],
  } = hit;
  const { formatPrice } = useContext(CartContext);
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency ?? 'USD';
  const { isProStaff } = useContext(CustomerContext);

  const isProduct = variants.length > 0;
  const linkUrl = isProduct ? `/${slug}` : link;

  const getCadDisplayPrice = () => (
    <div className=" text-sm">
      {isProStaff() && hit?.cadCompareAtPrice && (
        <p>{formatPrice(hit.cadCompareAtPrice)}</p>
      )}
      {isProStaff() && !hit?.cadCompareAtPrice && hit?.cadPrice && (
        <p>
          <s>{formatPrice(hit.cadPrice)}</s>{' '}
          {`${formatPrice(
            getPercentageDiscountPrice({
              price: hit.cadPrice,
              percentageDiscount: Number(
                process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE
              ),
            })
          )}`}
        </p>
      )}
      {!isProStaff() && hit?.cadPrice && <p>{formatPrice(hit.cadPrice)}</p>}
    </div>
  );

  const getDisplayPrice = () => (
    <div className=" text-sm">
      {isProStaff() && variants[0]?.compareAtPrice?.amount && (
        <p>{formatPrice(variants[0].price)}</p>
      )}
      {isProStaff() && !variants[0]?.compareAtPrice && (
        <p>
          <s>{formatPrice(variants[0].price)}</s>{' '}
          {`${formatPrice(
            getPercentageDiscountPrice({
              price: variants[0].price,
              percentageDiscount: Number(
                process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE
              ),
            })
          )}`}
        </p>
      )}
      {!isProStaff() && <p>{formatPrice(variants[0].price)}</p>}
    </div>
  );

  if (isProduct && currency === 'CAD' && !hit.cadPrice) {
    return null;
  }

  return (
    <div className="flex-1 border-b   first:border-t-0">
      <LinkComponent
        to={getInternalLink({ slug: linkUrl, currency })}
        onClick={() => {
          TrackClickAfterSearch(insights);
        }}
        state={{ searchedProduct: hit.__queryID ?? null }}
        tabIndex={-1}
        className="flex items-start no-underline gap-2 p-2 text-foreground-xdark hover:underline"
      >
        {image && (
          <div className="w-20 h-20 flex-shrink-0">
            <img
              src={`${image}${isProduct ? '&width=68' : ''}`}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div>
          <p className="text-base button-case m-0 font-medium line-clamp-2">
            {title}
          </p>
          {isProduct && currency === 'CAD' && getCadDisplayPrice()}
          {isProduct && currency !== 'CAD' && getDisplayPrice()}
          {!isProduct && <p className="text-sm line-clamp-2 m-0">{excerpt}</p>}
        </div>
      </LinkComponent>
    </div>
  );
};

export default SearchFormHit;
